/**
 * Soil-moisture sensor health checks.
 *
 * A probe can silently stop being useful — it stops reporting (dead battery,
 * lost gateway), or it reports the same number forever (buried in a dry pocket,
 * disconnected element), or its battery is about to die. None of these are
 * caught by the calibration or response checks, which assume live data. These
 * are pure, threshold-based observations surfaced to the user; the planner sets
 * and auto-clears them (no dismissal).
 *
 * All three are cheap. STALE needs only the denormalized `lastReading` and
 * LOW_BATTERY only the denormalized `lastBattery` (zero extra reads); FLATLINE
 * needs a batch of recent readings, so the planner runs it where readings are
 * already in hand.
 */
const HOUR_MS = 3_600_000;
/**
 * No reading for this many hours = STALE. Matches the planner's
 * MAX_READING_AGE_MS: the age at which planning stops trusting the sensor and
 * falls back to the ET model, so it is exactly when the user should know.
 */
export const SENSOR_STALE_HOURS = 24;
/** Look-back window for the flatline check. */
export const SENSOR_FLATLINE_HOURS = 72;
/** Need at least this many readings in the window to judge flatness. */
export const SENSOR_FLATLINE_MIN_READINGS = 24;
/**
 * Total range (percentage points) over the window at or below which the sensor
 * is FLATLINE. A live probe sees ET drawdown well above this over three days;
 * a range this tight means it isn't really measuring soil.
 */
export const SENSOR_FLATLINE_MAX_RANGE_PCT = 0.5;
/** Latest battery at or below this percent = LOW_BATTERY. */
export const SENSOR_LOW_BATTERY_PCT = 20;
const round1 = (n) => Math.round(n * 10) / 10;
/**
 * The most recent battery level reported by a sensor, or undefined if it has
 * never reported one. Prefers the dedicated `lastBattery` and falls back to a
 * battery riding along on `lastReading` — which covers sensors last written
 * before `lastBattery` existed, and any reading that lands with a battery
 * attached but does not advance `lastBattery` (an out-of-order post).
 */
export function latestBattery(sensor) {
    const onReading = sensor.lastReading?.batteryPct !== undefined
        ? { batteryPct: sensor.lastReading.batteryPct, at: sensor.lastReading.at }
        : undefined;
    const tracked = sensor.lastBattery;
    if (!tracked)
        return onReading;
    if (!onReading)
        return tracked;
    return onReading.at > tracked.at ? onReading : tracked;
}
/**
 * Assess a sensor's health at time `now`, returning every issue that applies
 * (issues can coexist — e.g. flatline AND low battery). `recentReadings` is
 * used only for the FLATLINE check; pass an empty array to skip it (STALE and
 * LOW_BATTERY still compute from the denormalized fields alone).
 */
export function assessSensorHealth(sensor, recentReadings, now) {
    const issues = [];
    const nowMs = now.getTime();
    const last = sensor.lastReading;
    // STALE: no reading, or the latest is older than the staleness horizon. A
    // sensor that has never reported is only stale once it has had a full
    // staleness window to do so — otherwise every freshly created sensor is born
    // flagged, before the user has even flashed the key into the meter.
    const lastMs = last ? Date.parse(last.at) : undefined;
    const createdMs = Date.parse(sensor.createdAt);
    const awaitingFirstReading = lastMs === undefined && Number.isFinite(createdMs) && nowMs - createdMs <= SENSOR_STALE_HOURS * HOUR_MS;
    if (!awaitingFirstReading && (lastMs === undefined || nowMs - lastMs > SENSOR_STALE_HOURS * HOUR_MS)) {
        issues.push(last ? { kind: 'STALE', lastReadingAt: last.at } : { kind: 'STALE' });
    }
    // LOW_BATTERY: judged from the latest battery reported, whenever that was.
    const battery = latestBattery(sensor);
    if (battery && battery.batteryPct <= SENSOR_LOW_BATTERY_PCT) {
        issues.push({ kind: 'LOW_BATTERY', batteryPct: battery.batteryPct });
    }
    // FLATLINE: enough readings in the window, but effectively no movement.
    const cutoff = nowMs - SENSOR_FLATLINE_HOURS * HOUR_MS;
    const inWindow = recentReadings.filter((r) => {
        const t = Date.parse(r.at);
        return Number.isFinite(t) && t >= cutoff && t <= nowMs;
    });
    if (inWindow.length >= SENSOR_FLATLINE_MIN_READINGS) {
        let min = Infinity;
        let max = -Infinity;
        let earliest = Infinity;
        let latest = -Infinity;
        for (const r of inWindow) {
            if (r.moisturePct < min)
                min = r.moisturePct;
            if (r.moisturePct > max)
                max = r.moisturePct;
            const t = Date.parse(r.at);
            if (t < earliest)
                earliest = t;
            if (t > latest)
                latest = t;
        }
        const range = max - min;
        if (range <= SENSOR_FLATLINE_MAX_RANGE_PCT) {
            issues.push({ kind: 'FLATLINE', hours: Math.round((latest - earliest) / HOUR_MS), rangePct: round1(range) });
        }
    }
    return issues;
}
//# sourceMappingURL=sensor-health.js.map